<template>
  <div v-if="visible" class="chat-overlay" @click.self="handleClose">
    <div class="chat-container">
      <div class="chat-header">
        <div class="chat-header-left">
          <MessageSquare size="20" />
          <span class="chat-title">订单 #{{ orderId }} 聊天</span>
        </div>
        <button class="close-btn" @click="handleClose">
          <X size="20" />
        </button>
      </div>

      <div class="chat-messages" ref="messagesContainer">
        <div class="empty-messages" v-if="messages.length === 0">
          <MessageSquare size="40" />
          <p>暂无消息，开始聊天吧</p>
        </div>
        <div v-else>
          <div
            v-for="(msg, index) in messages"
            :key="index"
            class="message"
            :class="{ 'message-mine': msg.sender === 'player', 'message-boss': msg.sender === 'boss' }"
          >
            <div class="message-bubble">
              <div class="message-sender">{{ msg.sender === 'player' ? '我' : '店长' }}</div>
              <div class="message-text">{{ msg.text }}</div>
              <div class="message-time">{{ formatTime(msg.timestamp) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="chat-input-area">
        <div class="chat-input-wrapper">
          <input
            v-model="inputText"
            type="text"
            placeholder="输入消息..."
            @keyup.enter="handleSend"
            class="chat-input"
          />
          <button class="send-btn" @click="handleSend" :disabled="!inputText.trim() || sending">
            <span v-if="!sending">发送</span>
            <span v-else>发送中...</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from 'vue'
import { supabase } from '@/utils/supabase'
import { chatManager } from '@/utils/chatManager'
import { MessageSquare, X } from 'lucide-vue-next'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  orderId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['close'])

const messages = ref([])
const inputText = ref('')
const sending = ref(false)
const messagesContainer = ref(null)

const storageKey = () => `chat-messages-${props.orderId}`

const loadMessages = () => {
  try {
    const saved = localStorage.getItem(storageKey())
    if (saved) {
      messages.value = JSON.parse(saved)
      console.log(`[ChatDialog] 加载了 ${messages.value.length} 条历史消息`)
    }
  } catch (e) {
    console.error('加载消息失败:', e)
  }
}

const saveMessages = () => {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(messages.value))
  } catch (e) {
    console.error('保存消息失败:', e)
  }
}

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}

const handleReceivedMessage = (message) => {
  if (message.text && message.sender) {
    const exists = messages.value.some(
      msg => msg.timestamp === message.timestamp && msg.text === message.text
    )
    if (!exists) {
      messages.value.push({
        text: message.text,
        sender: message.sender,
        timestamp: message.timestamp || new Date().toISOString()
      })
      saveMessages()
      if (props.visible) {
        scrollToBottom()
      }
    }
  }
}

const handleSend = async () => {
  const text = inputText.value.trim()
  if (!text || sending.value) return

  sending.value = true

  const message = {
    text: text,
    sender: 'player',
    timestamp: new Date().toISOString()
  }

  messages.value.push(message)
  saveMessages()

  try {
    console.log(`[ChatDialog] 正在发送消息:`, message)
    const { error } = await chatManager.sendMessage(props.orderId, message)

    if (error) {
      console.error('发送消息失败:', error)
      messages.value.pop()
      saveMessages()
    } else {
      console.log(`[ChatDialog] 消息发送成功`)
      inputText.value = ''
    }
  } catch (e) {
    console.error('发送消息异常:', e)
    messages.value.pop()
    saveMessages()
  } finally {
    sending.value = false
    scrollToBottom()
  }
}

const handleClose = () => {
  emit('close')
}

const formatTime = (timestamp) => {
  const date = new Date(timestamp)
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

onMounted(() => {
  console.log(`[ChatDialog] 组件挂载，开始订阅频道`)
  loadMessages()
  chatManager.subscribe(props.orderId, handleReceivedMessage)
  scrollToBottom()
})

watch(() => props.visible, (newVal) => {
  if (newVal) {
    loadMessages()
    scrollToBottom()
  }
})

watch(() => props.orderId, (newVal) => {
  if (newVal) {
    loadMessages()
    chatManager.subscribe(newVal, handleReceivedMessage)
    scrollToBottom()
  }
})

chatManager.init(supabase)
</script>

<style scoped>
.chat-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.chat-container {
  width: 100%;
  max-width: 400px;
  height: 80vh;
  max-height: 600px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.chat-header {
  padding: 16px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  font-weight: 600;
  font-size: 16px;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.chat-messages {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.03);
}

.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.chat-messages::-webkit-scrollbar-thumb {
  background: rgba(99, 102, 241, 0.5);
  border-radius: 3px;
}

.empty-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.4);
  gap: 10px;
}

.message {
  display: flex;
  margin-bottom: 12px;
}

.message-mine {
  justify-content: flex-end;
}

.message-boss {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 80%;
  padding: 12px 16px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.message-mine .message-bubble {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-boss .message-bubble {
  background: rgba(255, 255, 255, 0.12);
  color: white;
  border-bottom-left-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.message-sender {
  font-size: 12px;
  opacity: 0.8;
  margin-bottom: 6px;
  font-weight: 600;
}

.message-text {
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
}

.message-time {
  font-size: 11px;
  opacity: 0.6;
  margin-top: 6px;
  text-align: right;
}

.chat-input-area {
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.chat-input-wrapper {
  display: flex;
  gap: 10px;
}

.chat-input {
  flex: 1;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  color: white;
  font-size: 14px;
  outline: none;
  transition: all 0.3s ease;
}

.chat-input:focus {
  border-color: rgba(99, 102, 241, 0.8);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

.chat-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.send-btn {
  padding: 12px 24px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.send-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
}

.send-btn:active:not(:disabled) {
  transform: translateY(0);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>